import type { Menu as MenuApiResponse } from '@wix/ambassador-restaurants-menus-v1-menu/types';
import type {
  Section as MenuSectionApiResponse,
  Image,
} from '@wix/ambassador-restaurants-menus-v1-section/types';
import type { PopulatedItem } from '@wix/restaurants-populated-item-client/types';

import type { Money } from './businessTypes';

/** Raw menu shape returned by ambassador (all fields optional/nullable). */
export type { MenuApiResponse };
/** Raw section shape returned by ambassador (all fields optional/nullable). */
export type { MenuSectionApiResponse };

export type MenuSectionAvailability = NonNullable<
  MenuSectionApiResponse['orderSettings']
>['availability'];

/** Normalized menu object */
export type Menu = {
  id: string;
  revision: string;
  name?: string;
  description?: string | null;
  visible?: boolean;
  sectionIds: string[];
};

/** Normalized section object */
export type MenuSection = {
  id: string;
  revision: string;
  name?: string;
  description?: string | null;
  image?: Image;
  additionalImages?: Image[];
  visible?: boolean;
  itemIds: string[];
  orderSettings?: {
    availability?: MenuSectionAvailability;
  };
};

export type PopulatedMenu = Menu & {
  sections: PopulatedMenuSection[];
  size: number;
  truncated?: boolean;
};

export type PopulatedMenuSection = MenuSection & {
  size?: number;
  truncated?: boolean;
};

export type MenuItemPopulation = {
  labels: true;
  priceVariants: true;
  modifierGroups: true;
  modifiers: true;
};

/** Raw item shape returned by the populated-item client. */
export type MenuItemApiResponse = PopulatedItem<MenuItemPopulation>;

export enum PricingType {
  UNKNOWN_PRICING_TYPE = 'UNKNOWN_PRICING_TYPE',
  PRICE_VARIANTS = 'PRICE_VARIANTS',
  PRICE_INFO = 'PRICE_INFO',
  MARKET_PRICE = 'MARKET_PRICE',
}

/** Normalized item — replaces priceInfo string with a typed Money object. */
export type MenuItem = Omit<
  MenuItemApiResponse,
  'priceInfo' | 'pricingType'
> & {
  price: Money;
  pricingType?: PricingType;
};

export type MenuItemAvailability = NonNullable<
  MenuItem['orderSettings']
>['availability'];
